/**
 * Admin Login Page
 * Distinct design from Internal App - clean, professional layout
 *
 * SECURITY: Sign-up is disabled. Admin users must be pre-created via:
 * 1. Database seed (for initial super_admin)
 * 2. Invitation by existing admin
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { signIn } from "../lib/auth";
import { Loader2, Eye, EyeOff, AlertCircle } from "lucide-react";
import { LanguageSelector } from "../components/LanguageSelector";
import { getBuildInfo } from "../lib/buildInfo";
import {
  Input,
  Label,
  Button,
  Alert,
  AlertDescription,
} from "@jourfx/ui";

export function Login() {
  const { t } = useTranslation();
  const buildInfo = getBuildInfo();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const result = await signIn.email({
        email,
        password,
      });

      if (result.error) {
        setError(result.error.message || t("auth.loginFailed"));
        setIsLoading(false);
        return;
      }

      // Redirect to dashboard - use window.location to force full page reload
      // Admin user provisioning (linking Better Auth → adminUsers) is handled
      // by the useAdminAuth hook in AdminLayout after the Convex auth token
      // is properly established via the page reload.
      setIsLoading(false);
      window.location.href = "/";
    } catch (err) {
      console.error("Admin login failed:", err);
      setError(t("auth.errorOccurred"));
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen flex-col items-center bg-background">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: `radial-gradient(ellipse 90% 70% at 50% 35%, hsl(var(--primary) / 0.05) 0%, transparent 65%)`,
        }}
      />

      {/* Main content — flex-1 centers the form, version stays at bottom naturally */}
      <div className="relative z-10 flex flex-1 w-full max-w-md flex-col items-center justify-center px-6">
        <div className="w-full pb-8">
          {/* Logo */}
          <div className="mb-8 flex justify-center">
            <img
              src="/logo_jf.png"
              alt="JourFx"
              className="h-20 w-auto"
            />
          </div>

          {/* Login card */}
          <div className="overflow-hidden rounded-xl border border-border/40 bg-card shadow-md">
            <div className="h-0.5 bg-primary" />
            <div className="px-8 pb-8 pt-7">
              <h1 className="mb-6 text-lg font-semibold tracking-tight text-foreground sm:text-xl">
                {t("auth.loginTitle")}
              </h1>

              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive" className="animate-in fade-in-0 slide-in-from-top-1 duration-200">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {/* Email */}
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-[0.8125rem] font-medium">
                    {t("auth.email")}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="h-11"
                  />
                </div>

                {/* Password */}
                <div className="space-y-1.5">
                  <Label htmlFor="password" className="text-[0.8125rem] font-medium">
                    {t("auth.password")}
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                      autoComplete="current-password"
                      className="h-11 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Submit */}
                <div className="pt-1">
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="h-12 w-full font-medium shadow-sm transition-all hover:shadow-md hover:brightness-110"
                    size="lg"
                  >
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t("auth.login")}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Version — in document flow at page bottom */}
      <p className="relative z-10 pb-4 text-[0.6875rem] text-muted-foreground/60">
        v{buildInfo.appVersion}
      </p>

      <LanguageSelector />
    </div>
  );
}
